import { impactRatio, staggered, updateImpact } from './impact';
import { RULE } from './rules';
import { TAU, clamp, damp, rnd } from '../core/math';
import { RNG, rr } from '../core/rng';
import { enemies } from '../entities/enemies';
import { SFX } from '../platform/audio';
import { WHITE, scene } from '../render/scene';
import { STATE, angDiff, areaDamage, killEnemy, schedule, shake, spawnRing } from './fx';
import { hurtPlayer } from './player';
import { spawnEnemy } from './spawn';
import { swarmAI } from './enemy-ai';
import { bossAI } from './boss-ai';
import { warnCircle } from '../render/warn-fx';
import { STEP, clampArena, climbWaypoint, crushObstacles, groundYAt, losBlocked, resolveCircle } from '../world/arena';
import { P } from '../world/player';
import { ebullets, shocks } from '../world/pools';
/* ---------- 敵人更新 ---------- */
export var grid = {};
export function buildGrid() {
  grid = {};
  for (var i = 0; i < enemies.length; i++) {
    var e = enemies[i], k = ((e.pos.x / 4) | 0) + ',' + ((e.pos.z / 4) | 0);
    (grid[k] || (grid[k] = [])).push(e);
  }
}
export function eShoot(e, nx, nz, spread, speed, dmg, y?) {
  var b = ebullets.get(); if (!b) return;
  var a = Math.atan2(nx, nz) + spread;
  var y0 = y || (e.y + e.T.y + 1.6);
  b.m.position.set(e.pos.x + Math.sin(a) * e.r, y0, e.pos.z + Math.cos(a) * e.r);
  b.v.set(Math.sin(a) * speed, 0, Math.cos(a) * speed);
  /* **對空修正**：出膛時朝玩家當下的高度給一個垂直分量。
     規格說「近戰打不到你、遠程照打」——遠程要真的打得到，子彈就得會抬頭。
     直線外推（不是拋物線）是刻意的：玩家看得懂「它朝我來」，
     而且落點與水平版本一致，散佈與齊射的形狀完全不變。 */
  var dxz = Math.hypot(P.pos.x - e.pos.x, P.pos.z - e.pos.z);
  b.v.y = dxz > 0.5 ? clamp((P.y + 1.6 - y0) / (dxz / Math.max(1, speed)), -speed, speed) : 0;
  b.dmg = dmg; b.life = 0; scene.add(b.m);
}
export function spawnShock(x, z, dmg, rmax?, spd?) {
  var s = shocks.get(); if (!s) return;
  s.m.position.set(x, 0.14, z); s.r = 1; s.rmax = rmax; s.spd = spd; s.dmg = dmg; s.hit = false;
  s.m.scale.setScalar(1); s.m.material.opacity = 0.9; scene.add(s.m);
}

export function updateEnemies(dt) {
  buildGrid();
  var near = 0, near20 = 0;
  for (var i = enemies.length - 1; i >= 0; i--) {
    // 本輪迴圈中可能有多隻敵人被連帶移除（接觸傷害 → hurtPlayer → doRevive 會清掉周圍一圈），
    // 陣列因此在迭代途中縮短，不夾一下 i 就會讀到 undefined 而讓整個主迴圈拋例外、畫面凍住。
    if (i >= enemies.length) i = enemies.length - 1;
    if (i < 0) break;
    var e = enemies[i], T = e.T;

    /* 踉蹌：AI 與攻擊完全凍結，只保留位移慣性與受擊閃白。
       機體向側面傾倒，讓「這隻現在打得動」在畫面上一眼看得出來 ——
       增傷窗口若看不出來，玩家就不會學會集中火力。 */
    /* 踉蹌爆發：**踉蹌結束的那一步**引爆。接在這裡而不是 impact.ts 內部，
       是因為 areaDamage 會連鎖擊殺 —— 在敵人迴圈內呼叫才有既有的
       「陣列在迭代中縮短」防護（本檔開頭那段註解）。 */
    if (updateImpact(e, dt) && RULE.staggerBurst > 0) {
      areaDamage(e.pos.x, e.pos.z, 7 * (P.s.area || 1), RULE.staggerBurst, 0xffd24a, undefined, 'p-burst');
      spawnRing(e.pos.x, e.pos.z, 0xffd24a, 1, 8, 0.34);
    }
    if (staggered(e)) {
      e.vel.x *= 0.86; e.vel.z *= 0.86;
      e.pos.x += e.vel.x * dt; e.pos.z += e.vel.z * dt;
      e.root.position.set(e.pos.x, e.y, e.pos.z);
      e.root.rotation.z = Math.sin(e.staggerT * 11) * 0.22;
      if (e.flash > 0 || e.dirty) {
        e.flash = Math.max(0, e.flash - dt);
        // 小怪沒有自己的材質（型別共用），閃白走 instanceColor
        /* 踉蹌期間的閃白同理要收斂（見下面那一段的 `wk`）——
           Boss 踉蹌是增傷窗口，玩家要看得出來，但也要看得出**牠是什麼形狀**。 */
        var swk = e.T.bossRig ? 0.45 : 1;
        if (e.mats) {
          for (var smi = 0; smi < e.mats.length; smi++) {
            var sm = e.mats[smi];
            sm.emissive.copy(sm.userData.baseEm).lerp(WHITE, (0.55 + e.flash * 3) * swk);
            sm.emissiveIntensity = sm.userData.baseEi + 1.8 * swk;
          }
        }
        e.dirty = true;
      }
      continue;
    }
    e.root.rotation.z = 0;
    /* 出場放大動畫。目標尺度是 `eliteScale`（精英 1.7、一般 1）——
       寫死 1 的話精英長不大，玩家看不出這隻不一樣。 */
    var tgt = e.eliteScale || 1;
    if (e.root.scale.x < tgt) e.root.scale.setScalar(Math.min(tgt, e.root.scale.x + dt * 5 * tgt));

    var dx = P.pos.x - e.pos.x, dz = P.pos.z - e.pos.z;
    var dist = Math.hypot(dx, dz) || 0.001;
    /* 順手數「貼在玩家身上的敵人」。已經算過距離了，多這一行幾乎免費 ——
       另外開一輪鄰近查詢會在 300 隻時變成真的成本。 */
    if (dist < 9 && !T.boss) near++;
    /* 22 單位內的敵人數：**鏡頭用**（密度越高越拉遠，見 `sim/camera.ts`）。
       跟 near 一樣是順手數的 —— 距離這一行本來就要算，多一個計數幾乎免費。 */
    if (dist < 22) near20++;
    var nx = dx / dist, nz = dz / dist;

    // 掩體繞行：看不到玩家時沿切線方向滑動
    if (!T.boss && losBlocked(e.pos.x, e.pos.z, P.pos.x, P.pos.z, P.y)) {
      var ta = Math.atan2(nx, nz) + e.side * 0.95;
      nx = Math.sin(ta); nz = Math.cos(ta);
    }

    /* **玩家在高台上時，地面敵人去找坡**（地圖決定 6：高處只給視野與射界，
       不給安全）。沒有這一段的話高台就是安全屋 —— 敵人會擠在崖壁下
       原地推擠，玩家站上去就贏了。

       ⚠️ 比的是**玩家腳下的地面高度 `P.gy`** 而不是 `P.y`：玩家飛在
       半空中時 `P.gy` 還是 0，敵人不該為了一次滯空跑去爬坡（牠追不到，
       而且那 2.4 秒剛好把整群敵人送到場地另一頭）。 */
    if (!T.boss && !T.fly && e.marchT <= 0) {
      var wp = climbWaypoint(e.pos.x, e.pos.z, e.y, P.gy);
      if (wp) {
        var wx = wp.x - e.pos.x, wz = wp.z - e.pos.z, wd = Math.hypot(wx, wz) || 0.001;
        nx = wx / wd; nz = wz / wd;
      }
    }

    /* 行軍：照固定方向走，**完全不理玩家**（`sim/formations.ts` 設的）。
       橫掃牆與衝鋒之所以讀得出是「一道牆」而不是「一群敵人」，
       全靠這個 —— 只要它們各自追著玩家跑，隊形一秒就散掉。
       時間到就自動回到追擊，所以掃過去之後那些敵人還是有用的。 */
    if (e.marchT > 0) {
      e.marchT -= dt;
      nx = e.marchX; nz = e.marchZ;
    }

    /* 小怪一律走同一套狀態機（`sim/enemy-ai.ts`）——
       28 種的攻擊完全不同，但「預備→攻擊→恢復」的骨架是同一個。
       舊版是一連串 `e.type === 'charger'` 的內聯分支，那在 7 種時還行，
       28 種時會變成一個沒有人敢動的長函式。 */
    if (T.boss) bossAI(e, dt, nx, nz, dist, eShoot, spawnShock);
    else swarmAI(e, dt, nx, nz, dist, eShoot);

    /* 自爆型（DORN）把血歸零表示「我引爆了」。收屍放在這裡而不是載荷裡：
       `killEnemy` 會 splice `enemies`，在載荷裡呼叫的話這一圈剩下的程式
       還會繼續動一個已經回池的物件。`continue` 讓那一圈直接結束。 */
    if (e.hp <= 0 && e.alive) { killEnemy(e); continue; }

    // 分離
    var kx = (e.pos.x / 4) | 0, kz = (e.pos.z / 4) | 0, pushX = 0, pushZ = 0;
    for (var gx = -1; gx <= 1; gx++) for (var gz = -1; gz <= 1; gz++) {
      var cell = grid[(kx + gx) + ',' + (kz + gz)]; if (!cell) continue;
      for (var c = 0; c < cell.length; c++) {
        var o = cell[c]; if (o === e) continue;
        var ox = e.pos.x - o.pos.x, oz = e.pos.z - o.pos.z;
        var od = Math.hypot(ox, oz), min = e.r + o.r;
        if (od < min && od > 0.0001) { var f = (min - od) / min; pushX += ox / od * f; pushZ += oz / od * f; }
      }
    }
    e.vel.x += pushX * 22 * dt; e.vel.z += pushZ * 22 * dt;

    e.pos.x += e.vel.x * dt; e.pos.z += e.vel.z * dt;
    /* 地形碰撞：**浮空的東西與 Boss 不吃**。
       浮空型被地面構件擋住本來就說不通（規格 §4.2 的辨識靠「影子離身體很遠」）；
       Boss 則是比構件大得多，被一根柱子卡住會讀成 bug —— 牠們改成
       **把構件碾掉**（`crushObstacles`），順帶讓 KATHEDRALE 那一課
       「你躲的掩體會消失」在牠自己走路時就先示範一次。 */
    if (T.boss) {
      if (!T.fly && !e.airborne) crushObstacles(e.pos.x, e.pos.z, e.r * 0.72);
    } else if (!T.fly) {
      /* 爬升能力 `STEP`：矮台階直接踏上去，掩體與高台的側面照樣是牆。
         **敵人拿不到衝刺的 `VAULT`** —— 「衝刺翻得過去」是玩家的特權，
         給了敵人的話那條決定就不是一個選擇了。 */
      var rc = resolveCircle(e.pos.x, e.pos.z, e.r, e.y, STEP);
      e.pos.x = rc.x; e.pos.z = rc.z;
    }
    var ec = clampArena(e.pos.x, e.pos.z, 2);
    e.pos.x = ec.x; e.pos.z = ec.z;
    /* 貼地高度。**上坡瞬間跟上、下墜有速度** —— 兩邊都用 lerp 的話
       走上斜坡會整隻沉進坡裡，兩邊都瞬移的話走下高台像瞬間傳送。 */
    var egy = groundYAt(e.pos.x, e.pos.z);
    e.y = egy > e.y ? egy : Math.max(egy, e.y - 26 * dt);
    e.root.position.set(e.pos.x, e.y, e.pos.z);

    var tf = Math.atan2(dx, dz);
    /* 轉向速率。小怪用家族表自己的 `turn`（細長的 SPIKE 轉得快、
       蹲坐的 LADLE 轉得慢，那是剪影的一部分）；Boss 沿用舊值。 */
    e.face += angDiff(tf, e.face) * Math.min(1, dt * (T.turn ? T.turn * 2.2 : 7));
    e.root.rotation.y = e.face;

    e.phase += dt * (2 + Math.hypot(e.vel.x, e.vel.z) * 0.5);
    /* Boss 的造型動畫**不在這裡**（B2）。它們是真的階層 rig，動畫是每隻
       自己的 `upd(rig, o, dt)`，跑在**畫面幀**上（`render/boss-field.ts`）——
       跟機體動畫同一個理由：關節的平滑跑在固定 60Hz 上會被顯示率切成階梯。
       舊版那幾個固定掛鉤（`userData.spin/legs/arms`）隨著舊的四隻一起退場。 */
    if (e.flash > 0 || e.dirty) {
      e.flash = Math.max(0, e.flash - dt);
      var fl = e.flash / 0.10;
      /* 衝擊值越高機體越亮 —— 玩家要看得出「這隻快被打踉蹌了」，
         否則集中火力的報酬是隱形的，跟熱量犯一樣的錯。
         小怪的閃白走 `instanceColor`（材質是型別共用的，改它會整型別一起閃），
         所以這裡只處理有自己一份材質的 Boss。 */
      var ir = impactRatio(e);
      /* ⚠️ **BOSS 的閃白要收斂。** B2 之前 Boss 只有兩份材質（機身 + 發光），
         現在是整具 rig 的每一份都自己一份 —— 同一條式子套上去，
         主砲一開火整隻就變成一個純白色塊，剪影、分面、階段變形全部被蓋掉
         （實看截圖抓到的：WALZE 在戰場上是一團白色的東西）。
         0.45 是「看得出被打到，但形狀還在」的上限。 */
      var wk = e.T.bossRig ? 0.45 : 1;
      if (e.mats) {
        for (var mi = 0; mi < e.mats.length; mi++) {
          var m = e.mats[mi];
          m.emissive.copy(m.userData.baseEm).lerp(WHITE, Math.max(fl, ir * 0.6) * wk);
          m.emissiveIntensity = m.userData.baseEi + (fl * 2.6 + ir * 1.8) * wk;
        }
      }
      if (e.flash <= 0 && ir <= 0.01) e.dirty = false;
    }

    /* 接觸傷害**只有紅色有**（規格 §2.3）。橙／紫／天藍的 `T.dmg` 是 0，
       它們的威脅全部走各自的攻擊 —— 這條破例一次，四色語彙就開始說謊。 */
    /* `spinR` 是 SENSE 旋掃期間暫時放大的判定（橫刃是牠身體的延伸）。
       走接觸而不是另開一條傷害來源，紅色的語彙才不會說謊。 */
    if (T.dmg > 0 && dist < e.r + 1.3 + (e.spinR || 0)) {
      /* 觸及高度 = 牠自己的體型（`e.r * 2.6` ≈ 頭頂再高一點）。
         推進器飛到 12 單位就撞不到你了 —— 那是飛行最直接的用途。 */
      hurtPlayer(T.dmg * e.dmgMul * (e.buffDmg || 1), e.y + e.r * 2.6);
      e.vel.x -= nx * 12; e.vel.z -= nz * 12;
      if (e.st === 2) { e.st = 3; e.stT = T.tRec || 0.4; }   // 撲中了就進恢復，不會黏著你連撞
    }
  }
  STATE.nearP = near; STATE.near20 = near20;
}
